import dayjs from 'dayjs'

//Helper function to convert numeric date format (YYYYMMDD) to YYYY-MM-DD
export const convertNumericDateToString = (
  numericDate: number | string | null | undefined,
): string => {
  if (!numericDate) return ''

  const dateStr = numericDate.toString()
  if (dateStr.length === 8) {
    // Format: YYYYMMDD -> YYYY-MM-DD
    const year = dateStr.substring(0, 4)
    const month = dateStr.substring(4, 6)
    const day = dateStr.substring(6, 8)
    return `${year}-${month}-${day}`
  }

  return dateStr
}

export const convertNumericDateToDate = (
  numericDate: number
): Date => {
  const dateStr = numericDate.toString()
  // Format: YYYYMMDD -> YYYY-MM-DD
  const year = dateStr.substring(0, 4)
  const month = dateStr.substring(4, 6)
  const day = dateStr.substring(6, 8)
  return new Date(+year, +month - 1, +day);
}

//Helper function to convert date to integer in YYYYMMDD format
export const convertDateToInteger = (dateStr: string): number => {
  const dateParts = dateStr.split('-')
  const year = parseInt(dateParts[0])
  const month = parseInt(dateParts[1])
  const day = parseInt(dateParts[2])
  return year * 10000 + month * 100 + day
}

// Helper function to get previous business day
export const getPreviousBusinessDay = () => {
  const today = new Date()
  let previousDay = new Date(today)

  // Go back one day at a time until we find a weekday (Mon-Fri)
  do {
    previousDay.setDate(previousDay.getDate() - 1)
  } while (previousDay.getDay() === 0 || previousDay.getDay() === 6) // 0 = Sunday, 6 = Saturday

  return dayjs(previousDay).format('YYYY-MM-DD')
}

// Step a date back to the nearest weekday. Used when a date needs to be valid
// before it can be checked against a real selectable date range.
export const snapToWeekday = (date: dayjs.Dayjs): dayjs.Dayjs => {
  let result = date
  while (result.day() === 0 || result.day() === 6) {
    result = result.subtract(1, 'day')
  }
  return result
}

// Returns the following weekday after the specified date.
export const nextWeekday = (date: dayjs.Dayjs): dayjs.Dayjs => {
  let result = date.add(1, 'day');
  while (result.day() === 0 || result.day() === 6) {
    result = result.add(1, 'day')
  }
  return result;
}

// Returns the preceding weekday before the specified date.
export const previousWeekday = (date: dayjs.Dayjs): dayjs.Dayjs => {
  let result = date.subtract(1, 'day');
  while (result.day() === 0 || result.day() === 6) {
    result = result.subtract(1, 'day')
  }
  return result;
}

// Step a date forward to the nearest weekday (unlike nextWeekday, this does not advance
// off a date that's already a weekday). Used for presets whose begin date comes from a
// fixed calendar boundary (e.g. Jan 1) that must not slip into the prior period.
export const snapToWeekdayForward = (date: dayjs.Dayjs): dayjs.Dayjs => {
  let result = date
  while (result.day() === 0 || result.day() === 6) {
    result = result.add(1, 'day')
  }
  return result
}

// Every business day (inclusive) between begin and end, both 'YYYY-MM-DD'. Used to check
// whether a selected range has coverage for its whole span, not just its begin date —
// weekends are excluded since neither portfolio data nor reports exist for them.
export const getBusinessDaysInRange = (begin: string, end: string): string[] => {
  const days: string[] = []
  let cursor = dayjs(begin)
  const last = dayjs(end)
  while (!cursor.isAfter(last, 'day')) {
    const dow = cursor.day()
    if (dow !== 0 && dow !== 6) days.push(cursor.format('YYYY-MM-DD'))
    cursor = cursor.add(1, 'day')
  }
  return days
}

// Business days in [begin, end] the portfolio actually has data for — other business days in the
// span (market holidays) are excluded, since no report can ever exist for them and they can't be
// allowed to block a range's coverage check.
export const coverableDatesInRange = (begin: string, end: string, availableDates: string[]): string[] => {
  const availableDateSet = new Set(availableDates.map((d) => d.split('T')[0]))
  return getBusinessDaysInRange(begin, end).filter((d) => availableDateSet.has(d))
}

// Splits a range's coverable days (see coverableDatesInRange) by whether reportLibraryDateSet —
// the "already run" axis, expanding every library row's beginDate..endDate span — already has a
// report for them. Shared by the "already generated" preset highlight and Run Analysis's
// coverage check, so the two can't silently drift apart on what counts as covered.
export const splitReportCoverage = (
  begin: string,
  end: string,
  availableDates: string[],
  reportLibraryDateSet: Set<string>,
): { coverableDates: string[]; missingDates: string[]; coveredDates: string[] } => {
  const coverableDates = coverableDatesInRange(begin, end, availableDates)
  return {
    coverableDates,
    missingDates: coverableDates.filter((d) => !reportLibraryDateSet.has(d)),
    coveredDates: coverableDates.filter((d) => reportLibraryDateSet.has(d)),
  }
}

export const CUSTOM_RANGE_KEY = 'custom'

// Attribution date-range presets. End date is always the previous business day from today;
// the preset only determines how far back the begin date goes.
export const DATE_RANGE_PRESETS: { key: string; label: string; getBeginDate: (end: dayjs.Dayjs) => dayjs.Dayjs }[] = [
  // The backend rejects a beginDate/endDate pair that's exactly equal, so "1 Day" still needs a
  // distinct (earlier) begin — previousWeekday rather than a flat subtract(1, 'day') so it never
  // lands on a weekend the portfolio has no data for.
  { key: '1d', label: '1 Day', getBeginDate: (end) => previousWeekday(end) },
  { key: '1w', label: '1 Week', getBeginDate: (end) => end.subtract(1, 'week') },
  { key: '1m', label: '1 Month', getBeginDate: (end) => end.subtract(1, 'month') },
  { key: '3m', label: '3 Months', getBeginDate: (end) => end.subtract(3, 'month') },
  { key: '6m', label: '6 Months', getBeginDate: (end) => end.subtract(6, 'month') },
  { key: '1y', label: '1 Year', getBeginDate: (end) => end.subtract(1, 'year') },
  // Snapped forward, not via the caller's usual backward snap — Jan 1 must stay in the
  // current year even when it falls on a weekend, or YTD silently includes prior-year data.
  { key: 'ytd', label: 'YTD', getBeginDate: (end) => snapToWeekdayForward(end.startOf('year')) },
]
